import { useLayoutEffect, useState, useMemo } from "react";

import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

// project import
import NavItem from "./NavItem";
import NavGroup from "./NavGroup";
import menuItem from "menu-items";
import mitraPages from "menu-items/mitra";
import pages from "menu-items/pages";

import useConfig from "hooks/useConfig";
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from "config";
import { useSelector } from "react-redux";
import { selectUser } from "state/reducer/user";
import propertiesPages from "menu-items/properties";
// import { useGetMenuMaster } from 'api/menu';

import { createMongoAbility } from "@casl/ability";
import masterDataPages from "menu-items/master-data-page";
import adminPages from "menu-items/admin-page";
import utilityPages from "menu-items/utility";
import { FormattedMessage } from "react-intl";
import { HomeOutlined } from "@mui/icons-material";
import documentationPages from "menu-items/documentation";

// UA hanya boleh melihat menu "Unit" di dalam grup Properties.
const unitOnlyPropertiesPages = {
  ...propertiesPages,
  children: propertiesPages.children.filter((item) => item.id === "unit"),
};

// INT (internal, e.g. new SSO users) only gets the home page
const homePages = {
  id: "group-home",
  title: <FormattedMessage id="pages" />,
  type: "group",
  children: [
    {
      id: "home",
      title: <FormattedMessage id="home" />,
      type: "item",
      url: "/home",
      icon: () => <HomeOutlined sx={{ fontSize: 18 }} />,
      subject: "Home",
    },
  ],
};

// Peta kode role → grup menu yang tampil. Role yang tidak terdaftar = tanpa menu.
const ROLE_MENU_MAP = {
  ADM_RETAIL_NON: [pages, propertiesPages],
  HEAD_ADMIN_RETAIL_NON: [pages, propertiesPages],
  RS: [pages, propertiesPages],
  CA: [pages, propertiesPages],
  UEN: [utilityPages],
  HRS: [pages],
  HAF: [pages],
  HTT: [pages],
  PST: [utilityPages],
  UA: [unitOnlyPropertiesPages, utilityPages],
  COMPANY_ADMIN: [
    pages,
    propertiesPages,
    utilityPages,
    masterDataPages,
    adminPages,
  ],
  INT: [homePages],
  ADMIN: [pages, propertiesPages, utilityPages, masterDataPages, adminPages],
};

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { isDashboardDrawerOpened, isComponentDrawerOpened } = useSelector(
    (state) => state.menu,
  );

  const drawerOpen = isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const [selectedID, setSelectedID] = useState("");
  const [selectedItems, setSelectedItems] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  const profile = useSelector(selectUser).getProfile;

  const userRole = profile?.data?.role?.code || "";
  const userType = profile?.data?.user_type;

  // Create CASL ability from profile.data.abilities
  const ability = useMemo(() => {
    const rules = profile?.data?.abilities || [];
    return createMongoAbility(rules);
  }, [profile?.data?.abilities]);

  useLayoutEffect(() => {
    // Tentukan sumber menu berdasarkan role
    const roleItems =
      userType === "mitra" ? [mitraPages] : ROLE_MENU_MAP[userRole] || [];
    // note: dokumentasi tampil untuk semua role
    const sourceItems = [...roleItems, documentationPages];

    const filteredItems = {
      items: sourceItems.map((group) => ({
        ...group,
        children: group.children?.filter((item) => {
          if (!item.subject) return true;

          // Check permission using CASL ability
          return ability.can("read", item.subject);
        }),
      })),
    };

    setMenuItems(filteredItems);
  }, [userRole, userType, ability]);

  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items
      .slice(lastItem - 1, menuItems.items.length)
      .map((item) => ({
        title: item.title,
        elements: item.children,
        icon: item.icon,
        ...(item.url && {
          url: item.url,
        }),
      }));
  }

  const navGroups = menuItems.items
    .slice(0, lastItemIndex + 1)
    .map((item, index) => {
      switch (item.type) {
        case "group":
          if (item.url && item.id !== lastItemId) {
            return (
              <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
                {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
                <NavItem item={item} level={1} isParents />
              </List>
            );
          }

          return (
            <NavGroup
              key={item.id}
              setSelectedID={setSelectedID}
              setSelectedItems={setSelectedItems}
              setSelectedLevel={setSelectedLevel}
              selectedLevel={selectedLevel}
              selectedID={selectedID}
              selectedItems={selectedItems}
              lastItem={lastItem}
              remItems={remItems}
              lastItemId={lastItemId}
              item={item}
            />
          );
        default:
          return (
            <Typography key={item.id} variant="h6" color="error" align="center">
              Fix - Navigation Group
            </Typography>
          );
      }
    });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && { "& > ul:first-of-type": { mt: 0 } }),
        display: isHorizontal ? { xs: "block", lg: "flex" } : "block",
        height: "calc(100vh - 140px)",
        overflow: "auto",
      }}
    >
      {navGroups}
    </Box>
  );
}
